// ============================================================================
// ENTITY CREATION UTILITIES - Extracted for testability
// ============================================================================

import { NAMESPACES } from '@/lib/ontology/namespaces'

// ============================================================================
// FACET TYPE → ENTITY TYPE MAPPING
// AI sometimes returns facet-style types instead of entity types
// This maps them to valid ontology entity types (or null for insight-only)
// ============================================================================
export const FACET_TO_ENTITY_MAP: Record<string, string | null> = {
  // AI-generated facet-style types → entity types
  // Problem/Pain related
  'market_problem': 'Problem',
  'user_problem': 'Problem',
  'customer_problem': 'Problem',
  'pain_point': 'CustomerPain',
  'user_need': 'CustomerPain',
  'customer_pain': 'CustomerPain',
  'customer_job': 'CustomerJob',

  // Product/Solution related
  'product_concept': 'Feature',
  'product_feature': 'Feature',
  'solution_feature': 'Feature',
  'product_idea': 'Feature',

  // Strategy/Decision related
  'product_strategy': 'Decision',
  'strategic_decision': 'Decision',
  'business_decision': 'Decision',
  'business_goal': 'StrategicGoal',
  'strategic_goal': 'StrategicGoal',

  // Competition related
  'competitive_landscape': 'Competitor',
  'competitive_insight': 'Competitor',
  'competitor_analysis': 'Competitor',
  'market_competitor': 'Competitor',

  // Market/Trend related
  'market_trend': 'Trend',
  'industry_trend': 'Trend',
  'market_opportunity': 'Opportunity',

  // Constraints
  'technical_constraint': 'Constraint',
  'process_constraint': 'Constraint',
  'business_constraint': 'Constraint',

  // Risk related
  'business_risk': 'Risk',
  'market_risk': 'Risk',
  'technical_risk': 'Risk',

  // Insight-only types (no entity created)
  'stakeholder_feedback': null,
  'user_feedback': null,
  'feedback': null,
  'quote': null,
  'observation': null,
  'insight': null,
  'context': null,
  'note': null,
  'background': null,
}

/**
 * Resolves an entity type - either directly valid or via mapping
 * Returns null entityType for insight-only facets (no entity should be created)
 */
export function resolveEntityType(type: string): {
  entityType: string | null
  isInsightOnly: boolean
  originalType: string
} {
  // First check if it's already a valid entity type
  if (ENTITY_TYPE_NAMESPACES[type]) {
    return { entityType: type, isInsightOnly: false, originalType: type }
  }

  // Check if it can be mapped (case-insensitive)
  const lowerType = type.toLowerCase()
  if (lowerType in FACET_TO_ENTITY_MAP) {
    const mapped = FACET_TO_ENTITY_MAP[lowerType]
    return {
      entityType: mapped,
      isInsightOnly: mapped === null,
      originalType: type
    }
  }

  // Also check original case in map
  if (type in FACET_TO_ENTITY_MAP) {
    const mapped = FACET_TO_ENTITY_MAP[type]
    return {
      entityType: mapped,
      isInsightOnly: mapped === null,
      originalType: type
    }
  }

  // Unknown type - treat as insight-only to avoid creating invalid entities
  console.warn(`[entityCreation] Unknown entity type: "${type}" - treating as insight-only`)
  return { entityType: null, isInsightOnly: true, originalType: type }
}

// Map entity type names to their namespace and domain
export const ENTITY_TYPE_NAMESPACES: Record<string, { namespace: string; domain: string }> = {
  // Market domain
  Problem: { namespace: NAMESPACES.market, domain: 'market' },
  CustomerPain: { namespace: NAMESPACES.market, domain: 'market' },
  CustomerGain: { namespace: NAMESPACES.market, domain: 'market' },
  CustomerJob: { namespace: NAMESPACES.market, domain: 'market' },
  Persona: { namespace: NAMESPACES.market, domain: 'market' },
  Person: { namespace: NAMESPACES.market, domain: 'market' },
  Stakeholder: { namespace: NAMESPACES.market, domain: 'market' },
  CustomerSegment: { namespace: NAMESPACES.market, domain: 'market' },
  Alternative: { namespace: NAMESPACES.market, domain: 'market' },
  AlternativeDisadvantage: { namespace: NAMESPACES.market, domain: 'market' },
  AlternativeAdvantage: { namespace: NAMESPACES.market, domain: 'market' },
  Workaround: { namespace: NAMESPACES.market, domain: 'market' },
  Competitor: { namespace: NAMESPACES.market, domain: 'market' },
  Trend: { namespace: NAMESPACES.market, domain: 'market' },
  Industry: { namespace: NAMESPACES.market, domain: 'market' },
  MarketSignal: { namespace: NAMESPACES.market, domain: 'market' },

  // Business domain - Strategy
  Opportunity: { namespace: NAMESPACES.business, domain: 'business' },
  Threat: { namespace: NAMESPACES.business, domain: 'business' },
  Strength: { namespace: NAMESPACES.business, domain: 'business' },
  Weakness: { namespace: NAMESPACES.business, domain: 'business' },
  StrategicGoal: { namespace: NAMESPACES.business, domain: 'business' },
  Strategy: { namespace: NAMESPACES.business, domain: 'business' },
  ValueProposition: { namespace: NAMESPACES.business, domain: 'business' },
  Decision: { namespace: NAMESPACES.business, domain: 'business' },
  BusinessContext: { namespace: NAMESPACES.business, domain: 'business' },
  Risk: { namespace: NAMESPACES.business, domain: 'business' },
  Milestone: { namespace: NAMESPACES.business, domain: 'business' },
  GoToMarketStrategy: { namespace: NAMESPACES.business, domain: 'business' },
  Positioning: { namespace: NAMESPACES.business, domain: 'business' },

  // Business domain - Business Model Canvas
  Channel: { namespace: NAMESPACES.business, domain: 'business' },
  CustomerRelationship: { namespace: NAMESPACES.business, domain: 'business' },
  RevenueStream: { namespace: NAMESPACES.business, domain: 'business' },
  KeyResource: { namespace: NAMESPACES.business, domain: 'business' },
  KeyActivity: { namespace: NAMESPACES.business, domain: 'business' },
  KeyPartnership: { namespace: NAMESPACES.business, domain: 'business' },
  CostStructure: { namespace: NAMESPACES.business, domain: 'business' },
  SuccessMetric: { namespace: NAMESPACES.business, domain: 'business' },

  // Solution domain
  Product: { namespace: NAMESPACES.solution, domain: 'solution' },
  Theme: { namespace: NAMESPACES.solution, domain: 'solution' },
  Epic: { namespace: NAMESPACES.solution, domain: 'solution' },
  Feature: { namespace: NAMESPACES.solution, domain: 'solution' },
  Requirement: { namespace: NAMESPACES.solution, domain: 'solution' },
  UserStory: { namespace: NAMESPACES.solution, domain: 'solution' },
  Constraint: { namespace: NAMESPACES.solution, domain: 'solution' },
  ActionItem: { namespace: NAMESPACES.solution, domain: 'solution' },
  Dependency: { namespace: NAMESPACES.solution, domain: 'solution' },
  Pattern: { namespace: NAMESPACES.solution, domain: 'solution' },
}

// Counter for unique URI generation within same millisecond
let uriCounter = 0

// Reset counter for testing
export function resetUriCounter(): void {
  uriCounter = 0
}

// Get current counter for testing
export function getUriCounter(): number {
  return uriCounter
}

/**
 * Creates a unique entity URI from type and label
 */
export function createEntityUri(entityType: string, label: string): string {
  const config = ENTITY_TYPE_NAMESPACES[entityType]
  const namespace = config?.namespace ?? NAMESPACES.solution

  // Sanitize label for URI: keep only alphanumeric and underscores, limit length
  const sanitizedLabel = label
    .replace(/[^a-zA-Z0-9\s]/g, '') // Remove special chars
    .replace(/\s+/g, '_')           // Replace spaces with underscores
    .slice(0, 50)                   // Limit length for URI readability

  // Create unique suffix: timestamp + counter + random
  const uniqueSuffix = `${Date.now()}_${uriCounter++}_${Math.random().toString(36).slice(2, 6)}`

  return `${namespace}${entityType}_${sanitizedLabel}_${uniqueSuffix}`
}

/**
 * Escapes strings for use in SPARQL literals
 */
export function escapeSparqlString(str: string): string {
  return str
    .replace(/\\/g, '\\\\')  // Escape backslashes first
    .replace(/"/g, '\\"')    // Escape double quotes
    .replace(/\n/g, '\\n')   // Escape newlines
    .replace(/\r/g, '\\r')   // Escape carriage returns
    .replace(/\t/g, '\\t')   // Escape tabs
}

/**
 * Creates a SPARQL INSERT statement for an entity
 */
export function createEntitySparql(
  entityType: string,
  label: string,
  description?: string
): { sparql: string; uri: string } {
  const config = ENTITY_TYPE_NAMESPACES[entityType]
  const namespace = config?.namespace ?? NAMESPACES.solution
  const uri = createEntityUri(entityType, label)
  const typeUri = `${namespace}${entityType}`

  const escapedLabel = escapeSparqlString(label)
  const escapedDesc = description ? escapeSparqlString(description) : ''

  let sparql = `
    INSERT DATA {
      <${uri}> a <${typeUri}> .
      <${uri}> <${NAMESPACES.rdfs}label> "${escapedLabel}" .
  `

  if (description) {
    sparql += `  <${uri}> <${NAMESPACES.rdfs}comment> "${escapedDesc}" .\n`
  }

  sparql += '}'

  return { sparql, uri }
}

/**
 * Validates that a mapping decision has all required data for entity creation
 * Now uses resolveEntityType to map facet-style types to valid entity types
 */
export function validateDecisionForCreation(decision: {
  facetId: string
  action: string
  newEntityType?: string
  aiSuggestedType?: string
  newEntityLabel?: string
}, facetContent?: string): {
  isValid: boolean
  entityType: string | null
  entityLabel: string | null
  reason?: string
  isInsightOnly?: boolean
} {
  // Only process create_new or accept_suggestion
  if (decision.action !== 'create_new' && decision.action !== 'accept_suggestion') {
    return {
      isValid: false,
      entityType: null,
      entityLabel: null,
      reason: `Action '${decision.action}' does not create entities`,
    }
  }

  // Determine raw entity type from decision
  const rawType = decision.newEntityType || decision.aiSuggestedType || null

  if (!rawType) {
    return {
      isValid: false,
      entityType: null,
      entityLabel: null,
      reason: 'Missing entity type (no newEntityType or aiSuggestedType)',
    }
  }

  // Resolve the type - may map facet-style to entity type, or mark as insight-only
  const resolved = resolveEntityType(rawType)

  if (resolved.isInsightOnly) {
    return {
      isValid: false,
      entityType: null,
      entityLabel: null,
      reason: `Type '${rawType}' is insight-only (no entity created)`,
      isInsightOnly: true,
    }
  }

  if (!resolved.entityType) {
    return {
      isValid: false,
      entityType: null,
      entityLabel: null,
      reason: `Unknown type '${rawType}' cannot be mapped to an entity`,
    }
  }

  // Determine entity label
  const entityLabel = decision.newEntityLabel || facetContent?.slice(0, 100) || null

  if (!entityLabel) {
    return {
      isValid: false,
      entityType: resolved.entityType,
      entityLabel: null,
      reason: 'Missing entity label (no newEntityLabel or facet content)',
    }
  }

  return {
    isValid: true,
    entityType: resolved.entityType,
    entityLabel,
  }
}
